<template>
  <RygenDropdown
    v-bind="$attrs"
    :id="id"
    :model-value="modelValue"
    :disabled="options.length === 1"
    :loading="loading"
    :options="options"
    option-label="name"
    data-key="id"
    @update:model-value="emit('update:model-value', $event)"
  />
</template>

<script lang="ts" setup>
import axios from 'axios';
import { Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { DomainReference } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: DomainReference,
    default: () => undefined,
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: DomainReference): void;
}>();

const { selectedDomainId } = useCurrentUserStore();
const { t } = useI18n();
const { owningClient } = useParentEntityStore();
const toast = useToast();

const options: Ref<DomainReference[]> = ref([]);
const loading = ref(false);

const loadOptions = async () => {
  if (!owningClient.value?.id) {
    return;
  }

  try {
    loading.value = false;
    options.value = await api.billTo.getBillToInvoicePayingPartyOptions();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading invoice paying party options', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.invoicePayingParties', 2),
      }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

watch(options, (newVal: DomainReference[]) => {
  if (newVal.length === 1 && !modelValue?.value) {
    emit('update:model-value', newVal[0]);
  }
});

watch(
  selectedDomainId,
  () => {
    loadOptions();
  },
  { immediate: true },
);
</script>
